import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { buildApiUrl } from "../../config/api";
import { downloadStaffRosterTemplate, parseStaffRosterFile } from "../../utils/bulkStaffRoster";
import { getStaffRoleOptions, normalizeStaffRole } from "../../utils/staffRoles";

export default function AddStaffRosterDialog({ open, onClose, onStaffAdded, schoolId }) {
    const fileInputRef = useRef(null);
    const [tab, setTab] = useState(0);
    const [name, setName] = useState("");
    const [role, setRole] = useState("Teacher");
    const [bulkText, setBulkText] = useState("");
    const [fileName, setFileName] = useState("");
    const [parsedEntries, setParsedEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    const resetState = () => {
        setTab(0);
        setName("");
        setRole("Teacher");
        setBulkText("");
        setFileName("");
        setParsedEntries([]);
        setLoading(false);
        setError("");
        setResult(null);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    useEffect(() => {
        if (!open) return;
        resetState();
    }, [open]);

    const submitRoster = async (payload) => {
        if (!schoolId) {
            setError("School account not loaded. Try again.");
            return;
        }
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(
                buildApiUrl(`/StaffAttendanceRegister/${schoolId}/roster`),
                payload,
            );
            setResult(data);
            onStaffAdded?.(data);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Could not add names");
        } finally {
            setLoading(false);
        }
    };

    const handleSingleSubmit = async (event) => {
        event.preventDefault();
        const trimmedName = name.trim();
        if (!trimmedName) {
            setError("Enter a name.");
            return;
        }
        await submitRoster({
            entries: [{ name: trimmedName, role: normalizeStaffRole(role) }],
        });
    };

    const handleBulkSubmit = async () => {
        const text = bulkText.trim();
        if (!text) {
            setError("Paste at least one name — one per line.");
            return;
        }
        await submitRoster({ text });
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setError("");
        try {
            const entries = await parseStaffRosterFile(file);
            setFileName(file.name);
            setParsedEntries(entries);
            if (!entries.length) {
                setError("No names found in the file. Use columns: name, role.");
            }
        } catch {
            setError("Could not read that file. Use CSV with name and optional role.");
        }
    };

    const handleFileSubmit = async () => {
        if (!parsedEntries.length) {
            setError("Upload a file with at least one name.");
            return;
        }
        const normalizedEntries = parsedEntries.map((entry) => ({
            ...entry,
            role: normalizeStaffRole(entry.role),
        }));
        await submitRoster({ entries: normalizedEntries });
    };

    if (result?.addedCount != null) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Names added</DialogTitle>
                <DialogContent>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Added {result.addedCount} staff to the sign-in book.
                        {result.skippedCount > 0
                            ? ` ${result.skippedCount} row(s) were skipped.`
                            : ""}
                    </Alert>
                    {result.skippedCount > 0 && Array.isArray(result.skipped) ? (
                        <Typography variant="body2" color="text.secondary">
                            Skipped:{" "}
                            {result.skipped
                                .slice(0, 5)
                                .map((row) => row.name || "row")
                                .join(", ")}
                            {result.skipped.length > 5 ? "…" : ""}
                        </Typography>
                    ) : null}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        To give someone portal login later or change their role, use the
                        <Link component={RouterLink} to="/Admin/teachers">
                            {' '}Staff list
                        </Link>.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="contained" onClick={handleClose}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add names to sign-in book</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Names only — no login credentials here. Once these people are added to the sign-in book,
                    you can enable their portal access and assign a proper staff role later from the
                    <Link component={RouterLink} to="/Admin/teachers" onClick={handleClose}>
                        {' '}Staff list
                    </Link>.
                </Typography>

                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                ) : null}

                <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
                    <Tab label="One name" />
                    <Tab label="Paste list" />
                    <Tab label="Upload file" />
                </Tabs>

                {tab === 0 ? (
                    <Box component="form" id="add-staff-roster-single" onSubmit={handleSingleSubmit}>
                        <Stack spacing={2}>
                            <TextField
                                autoFocus
                                required
                                label="Full name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Grace Mwale"
                            />
                            <FormControl fullWidth>
                                <InputLabel>Role</InputLabel>
                                <Select
                                    label="Role"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                >
                                    {getStaffRoleOptions().map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {getStaffRoleOptions().find((o) => o.value === role)?.description && (
                                <Typography variant="caption" color="text.secondary">
                                    {getStaffRoleOptions().find((o) => o.value === role)?.description}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                ) : null}

                {tab === 1 ? (
                    <Stack spacing={2}>
                        <TextField
                            multiline
                            minRows={8}
                            label="Names"
                            value={bulkText}
                            onChange={(e) => setBulkText(e.target.value)}
                            placeholder={"One name per line\nOptional: Name, Role\n\nJane Banda, Teacher\nJohn Phiri, Head of Department"}
                            helperText="One person per line. Add a comma and role if needed. Roles: Teacher, Form Teacher, Class Teacher, Bursar, Head of Department, Librarian — or leave blank for Teacher."
                        />
                    </Stack>
                ) : null}

                {tab === 2 ? (
                    <Stack spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={downloadStaffRosterTemplate}
                            sx={{ alignSelf: "flex-start" }}
                        >
                            Download CSV template
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.txt,text/csv"
                            hidden
                            onChange={handleFileChange}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CloudUploadOutlinedIcon />}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            Upload Staff CSV
                        </Button>
                        {fileName ? (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    {fileName} — {parsedEntries.length} staff member(s) ready
                                </Typography>
                                {parsedEntries.length > 0 && (
                                    <>
                                        <TableContainer sx={{ maxHeight: 300 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: "action.hover" }}>
                                                        <TableCell>Name</TableCell>
                                                        <TableCell>Role</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {parsedEntries.slice(0, 5).map((entry, idx) => {
                                                        const normalized = normalizeStaffRole(entry.role);
                                                        const roleOpt = getStaffRoleOptions().find(
                                                            (o) => o.value === normalized,
                                                        );
                                                        return (
                                                            <TableRow key={idx}>
                                                                <TableCell>{entry.name}</TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        label={roleOpt?.label || normalized}
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {parsedEntries.length > 5 && (
                                            <Typography variant="caption" color="text.secondary">
                                                + {parsedEntries.length - 5} more
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                            Roles not recognized will default to Teacher.
                                        </Typography>
                                    </>
                                )}
                            </>
                        ) : null}
                    </Stack>
                ) : null}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                {tab === 0 ? (
                    <Button
                        type="submit"
                        form="add-staff-roster-single"
                        variant="contained"
                        startIcon={<PersonAddAlt1OutlinedIcon />}
                        disabled={loading}
                    >
                        {loading ? "Adding…" : "Add name"}
                    </Button>
                ) : null}
                {tab === 1 ? (
                    <Button variant="contained" onClick={handleBulkSubmit} disabled={loading}>
                        {loading ? "Adding…" : "Add names"}
                    </Button>
                ) : null}
                {tab === 2 ? (
                    <Button
                        variant="contained"
                        onClick={handleFileSubmit}
                        disabled={loading || !parsedEntries.length}
                    >
                        {loading ? "Importing…" : `Import ${parsedEntries.length || ""} staff members`}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}
